// # Duration Stats Component
import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp, FaClock, FaHourglassHalf, FaCheckCircle } from 'react-icons/fa';
import './chp_duartion_stats2.css';

// # Import chapter icons mapping for step2
import anatomyIcon from '../step1/step1_icons/anatomy.png';
import biochemistryIcon from '../step1/step1_icons/biochemistry.png';
import bloodIcon from '../step1/step1_icons/blood.png';
import cnsIcon from '../step1/step1_icons/cns.png';
import earIcon from '../step1/step1_icons/ear.png';
import endocrineIcon from '../step1/step1_icons/endocrine.png';
import emergencyIcon from '../step1/step1_icons/emergency.png';
import eyeIcon from '../step1/step1_icons/eye.png';
import gitIcon from '../step1/step1_icons/git.png';
import healthcareIcon from '../step1/step1_icons/healthcare.png';
import heartIcon from '../step1/step1_icons/heart.png';
import immunologyIcon from '../step1/step1_icons/immunology.png';
import infectiousIcon from '../step1/step1_icons/infectious.png';
import kidneyIcon from '../step1/step1_icons/kidney.png';
import lungsIcon from '../step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../step1/step1_icons/newborn.png';
import pharmaIcon from '../step1/step1_icons/pharma.png';
import psychiatryIcon from '../step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../step1/step1_icons/public_health.png';
import qualityIcon from '../step1/step1_icons/quality.png';
import reproductiveIcon from '../step1/step1_icons/reproductive-system.png';
import skinIcon from '../step1/step1_icons/skin.png';
import statisticsIcon from '../step1/step1_icons/statistics.png';
import surgeryIcon from '../step1/step1_icons/surgery.png';
import wellPatientIcon from '../step1/step1_icons/well_patient.png';

// # Chapter icons mapping for Step 2 (lowercase names matching Firestore keys)
const chapterIcons = {
  cardiovascular: heartIcon,
  respiratory_and_ent: lungsIcon,
  renal_and_urology: kidneyIcon,
  endocrine: endocrineIcon,
  hematology_and_oncology: bloodIcon,
  immunology: immunologyIcon,
  git: gitIcon,
  musculoskeletal: musculoskeletalIcon,
  neurology: cnsIcon,
  ophthalmology: eyeIcon,
  psychiatry: psychiatryIcon,
  dermatology: skinIcon,
  gynecology_and_obstetrics: reproductiveIcon,
  neonate_and_infant: newbornIcon,
  genetics: biochemistryIcon,
  infectious_disease: infectiousIcon,
  surgery_and_anesthesia: surgeryIcon,
  emergency_trauma_and_toxicology: emergencyIcon,
  the_well_patient: wellPatientIcon,
  biostatistics_and_epidemiology: statisticsIcon,
  ethics_and_communication: publicHealthIcon,
  healthcare_delivery: healthcareIcon,
  quality_and_safety: qualityIcon,
  pharmacology_review: pharmaIcon
};

// # Helper function to calculate duration between two dates
const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end - start;

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes };
};

// # Helper function to format duration
const formatDuration = (duration) => {
  if (!duration) return 'Not completed';

  const parts = [];
  if (duration.days > 0) parts.push(`${duration.days}d`);
  if (duration.hours > 0) parts.push(`${duration.hours}h`);
  if (duration.minutes > 0) parts.push(`${duration.minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '< 1m';
};

function ChapterDurationStats({ libraryData }) {
  const [expandedChapters, setExpandedChapters] = useState(new Set());
  const [chapterStats, setChapterStats] = useState([]);

  useEffect(() => {
    if (!libraryData?.step2) {
      setChapterStats([]);
      return;
    }

    try {
      const stats = Object.entries(libraryData.step2)
        .map(([chapterId, chapterData]) => {
          if (!chapterData || typeof chapterData !== 'object') {
            console.warn(`Invalid chapter data for ${chapterId}`);
            return null;
          }

          // # Calculate chapter duration
          const chapterDuration = calculateDuration(
            chapterData.ChptStartedAt,
            chapterData.iscomplete_at
          );

          // # Calculate topic durations
          const topicStats = Object.entries(chapterData)
            .filter(([key]) => key !== 'iscomplete' && key !== 'iscomplete_at' && 
                              key !== 'ChptStartedAt' && key !== 'IsChapStarted')
            .map(([topicId, topicData]) => {
              if (!topicData || typeof topicData !== 'object') {
                console.warn(`Invalid topic data for ${chapterId}/${topicId}`);
                return null;
              }

              return {
                id: topicId,
                name: topicId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                duration: calculateDuration(topicData.TopicStartedAt, topicData.completedAt),
                isComplete: topicData.completed,
                startDate: topicData.TopicStartedAt,
                endDate: topicData.completedAt
              };
            })
            .filter(topic => topic && topic.startDate); // # Filter out invalid and not started topics

          if (!topicStats.length) {
            return null;
          }

          return {
            id: chapterId,
            name: chapterId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
            icon: chapterIcons[chapterId],
            duration: chapterDuration,
            isComplete: chapterData.iscomplete,
            startDate: chapterData.ChptStartedAt,
            endDate: chapterData.iscomplete_at,
            topics: topicStats,
            totalTopics: topicStats.length,
            completedTopics: topicStats.filter(t => t.isComplete).length
          };
        })
        .filter(chapter => chapter && chapter.startDate && chapter.topics.length > 0)
        .sort((a, b) => {
          if (!a.duration || !b.duration) return 0;
          const aDuration = (a.duration.days * 24 * 60) + (a.duration.hours * 60) + a.duration.minutes;
          const bDuration = (b.duration.days * 24 * 60) + (b.duration.hours * 60) + b.duration.minutes;
          return bDuration - aDuration;
        });

      setChapterStats(stats);
    } catch (error) {
      console.error('Error processing library data:', error);
      setChapterStats([]);
    }
  }, [libraryData]);

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  return (
    <div className="chpdur2_container">
      <div className="chpdur2_header">
        <div className="chpdur2_header-icon">
          <FaHourglassHalf />
        </div>
        <p>Track how long it takes to complete each chapter and its topics</p>
      </div>

      <div className="chpdur2_stats-list">
        {chapterStats.map(chapter => (
          <div key={chapter.id} className="chpdur2_chapter-card">
            <div 
              className="chpdur2_chapter-header"
              onClick={() => toggleChapter(chapter.id)}
            >
              <div className="chpdur2_chapter-icon">
                <img src={chapter.icon} alt={chapter.name} />
                {chapter.isComplete && (
                  <div className="chpdur2_completion-badge">
                    <FaCheckCircle />
                  </div>
                )}
              </div>
              
              <div className="chpdur2_chapter-info">
                <div className="chpdur2_chapter-title">
                  <h3>{chapter.name}</h3>
                  {chapter.isComplete && (
                    <span className="chpdur2_completion-status">Completed</span>
                  )}
                </div>
                <div className="chpdur2_completion-info">
                  <FaClock className="chpdur2_clock-icon" />
                  <span>
                    {chapter.isComplete 
                      ? formatDuration(chapter.duration)
                      : <span className="chpdur2_in-progress">In Progress</span>
                    }
                  </span>
                </div>
              </div>

              <div className="chpdur2_expand-icon">
                {expandedChapters.has(chapter.id) ? <FaChevronUp /> : <FaChevronDown />}
              </div>
            </div>

            {expandedChapters.has(chapter.id) && (
              <div className="chpdur2_topic-list">
                {chapter.topics.map(topic => (
                  <div key={topic.id} className="chpdur2_topic-item">
                    <div className="chpdur2_topic-name">{topic.name}</div>
                    <div className="chpdur2_topic-duration">
                      {topic.isComplete ? (
                        <>
                          <FaClock className="chpdur2_clock-icon" />
                          <span>{formatDuration(topic.duration)}</span>
                        </>
                      ) : (
                        <span className="chpdur2_in-progress">In Progress</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ChapterDurationStats;
